/**
 * header.js — Mega menu (desktop) + mobile drawer
 * Step 3.3b.2/3.3b.3
 *
 * Desktop (mega menu):
 *   Hover/focus-within open is CSS-only (primary mechanism).
 *   JS syncs aria-expanded so stale state never accumulates:
 *     - mouseenter on any group → closeAll(thatGroup): clears aria-expanded
 *       on all other groups so a click-opened panel doesn't stay "locked"
 *       while the user hovers to a sibling dropdown.
 *     - mouseleave on the whole nav → closeAll(null) after 150ms delay,
 *       cancelled on re-entry, so fast mouse movements through the
 *       hover-bridge don't prematurely close the panel.
 *   Click toggle and Escape/keyboard path remain for touch + a11y.
 *   aria-expanded on <button> only (not on .has-dropdown div).
 *   aria-hidden synced on .mega panel for JS-triggered paths.
 *
 * Mobile (drawer):
 *   data-open="true/false" on #feel-mobile-menu controls visibility.
 *   aria-expanded on toggle button, aria-hidden on drawer panel.
 *   Scroll lock via body.feel-drawer-open.
 *   Accordion uses native <details> — no single-open JS needed.
 */

(function () {
  'use strict';

  /* ── Desktop mega menu ─────────────────────────────────────────────── */

  function initMegaMenu() {
    const triggers = document.querySelectorAll('.has-dropdown > .feel-header__nav-item');
    if (!triggers.length) return;

    const nav = document.querySelector('.feel-header__nav');
    let leaveTimer = null;

    function getMegaPanel(group) {
      return group.querySelector('.mega');
    }

    function closeGroup(btn, group) {
      btn.setAttribute('aria-expanded', 'false');
      const panel = getMegaPanel(group);
      if (panel) panel.setAttribute('aria-hidden', 'true');
    }

    function closeAll(exceptGroup) {
      document.querySelectorAll('.has-dropdown > button[aria-expanded="true"]').forEach(function (openBtn) {
        const group = openBtn.parentElement;
        if (group === exceptGroup) return;
        closeGroup(openBtn, group);
      });
    }

    triggers.forEach(function (btn) {
      const group = btn.parentElement; // .has-dropdown div
      const panel = getMegaPanel(group);

      // Entering a group: cancel pending leave-close and clear stale
      // aria-expanded on every other group. Keeps at most one group "JS-open".
      group.addEventListener('mouseenter', function () {
        clearTimeout(leaveTimer);
        closeAll(group);
      });

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        closeAll(isOpen ? null : group);
        btn.setAttribute('aria-expanded', String(!isOpen));
        if (panel) panel.setAttribute('aria-hidden', String(isOpen)); // inverse: open → false
      });

      group.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          closeGroup(btn, group);
          btn.focus();
        }
      });
    });

    // Leaving the whole nav: close all JS-held open states after 150ms.
    // Delay absorbs fast mouse movement through the ::before hover bridge.
    // Re-entering nav (or any group) cancels the timer.
    if (nav) {
      nav.addEventListener('mouseleave', function () {
        leaveTimer = setTimeout(function () { closeAll(null); }, 150);
      });
      nav.addEventListener('mouseenter', function () {
        clearTimeout(leaveTimer);
      });
    }

    // Outside click: link inside .mega is inside .feel-header__nav so
    // closest() returns nav → closeAll NOT called → navigation proceeds normally.
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.feel-header__nav')) {
        closeAll(null);
      }
    });
  }

  /* ── Mobile drawer ─────────────────────────────────────────────────── */

  function initMobileMenu() {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    if (!menuToggle || !mobileMenu) return;

    function closeMobileMenu() {
      mobileMenu.dataset.open = 'false';
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileMenu.setAttribute('inert', ''); // a11y: remove closed menu from tab order (aria-hidden-focus)
      menuToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('feel-drawer-open');
    }

    function openMobileMenu() {
      mobileMenu.dataset.open = 'true';
      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileMenu.removeAttribute('inert'); // a11y: focusable again when open (must precede focus())
      menuToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('feel-drawer-open');
      // Move focus to first interactive element in drawer
      const firstFocusable = mobileMenu.querySelector(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) firstFocusable.focus();
    }

    menuToggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = mobileMenu.dataset.open === 'true';
      if (isOpen) { closeMobileMenu(); menuToggle.focus(); }
      else openMobileMenu();
    });

    // [data-menu-close] buttons inside the drawer
    mobileMenu.querySelectorAll('[data-menu-close]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeMobileMenu();
        menuToggle.focus();
      });
    });

    // Close when a real navigation link is clicked (href not starting with #)
    mobileMenu.addEventListener('click', function (e) {
      const link = e.target.closest('a[href]');
      if (link && !link.getAttribute('href').startsWith('#')) {
        closeMobileMenu();
      }
    });

    // Escape closes and returns focus to toggle
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileMenu.dataset.open === 'true') {
        closeMobileMenu();
        menuToggle.focus();
      }
    });
  }

  /* ── Init ──────────────────────────────────────────────────────────── */

  function init() {
    initMegaMenu();
    initMobileMenu();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
